import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { MapObject } from '@wehunt/ui'
import { listTeamMapPins } from './api/teamMapPinsApi'
import { dtoToMapObject } from './mapDtoMappers'
import { mapPinImageUrl } from './mapPinImages'

export { dtoToMapObject }

/**
 * Resolves the display download URL for each EXISTING image on a pin. The pin
 * doc only stores bare ids; the editor renders `url`, so it is filled in here.
 * Exported because a hunter's PRIVATE pins (see useMyMapPins) come from a
 * different route but the same document shape, and resolving their pictures is
 * the same problem — a second copy of this would be a second place for the
 * swallow-vs-drop decision below to drift.
 *
 * A failure to resolve one id (e.g. the object is gone) is swallowed and leaves
 * that image's `url` empty rather than dropping the id — the id must survive in
 * the loaded object so it round-trips into the pin's `images` list on a save
 * the user did not touch the picture in. Newly-staged images (carrying a
 * `file`) already have a local object URL and are passed through untouched.
 */
/**
 * One shared empty list, so a query that has not answered yet returns the SAME
 * value every render. A fresh `[]` each time changes the identity of everything
 * built from it — and the team map view re-frames the whole ground whenever the
 * map object it is handed changes identity, which is what reset a reader's zoom
 * on every unrelated re-render.
 */
const NO_PINS: MapObject[] = []

export async function resolveImageUrls(obj: MapObject): Promise<MapObject> {
  if (!obj.photos || obj.photos.length === 0) return obj
  const photos = await Promise.all(
    obj.photos.map(async (img) => {
      if (!('id' in img)) return img
      try {
        return { id: img.id, url: await mapPinImageUrl(img.id) }
      } catch {
        return img
      }
    }),
  )
  return { ...obj, photos }
}

export function useGroupMapPins(groupId: string): MapObject[] {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'

  const { data } = useQuery({
    queryKey: ['clubhouse', 'teams', groupId, 'map-pins'],
    enabled: isSignedIn && !!groupId,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      const dtos = await listTeamMapPins(groupId, token)
      return Promise.all(dtos.map((dto) => resolveImageUrls(dtoToMapObject(dto))))
    },
  })

  return data ?? NO_PINS
}
